import React, { useState } from 'react'
import moment from 'moment'
import { isEmpty, trim, compose, defaultTo, prop, pathOr, head, toString } from 'ramda'
import { useForm, useFetch } from '../../../../hooks'
import Button from '../../../../components/inputs/Button'
import Buttons from '../../../../modules/Buttons'
import { FormHeader, FormBody, FormBodyText, Icon, Form } from '../../../../modules/Form/styled'
import { getDataByKey, parseMygrplist } from '../../../../helpers'
import DatePicker from '../../../../components/DatePicker'
import FromInput from '../../../../components/inputs/FromInput'
import FromSelect from '../../../../components/inputs/FromSelect'
import Checkbox from '../../../../components/inputs/Checkbox'

const FormAddCow = ({ onClickCancel, doFetchHerd }) => {
    const { response, doFetch } = useFetch({
        url: '/rpc/add_cow_adv',
        method: 'POST'
    })

    const { response: resModalData } = useFetch({
        url: '/get_newcow_modal_data',
        method: 'GET'
    })

    const modalData = getDataByKey(resModalData, 'data', null);

    const [lastBirthDate, setLastBirthDate] = useState(null)
    const [isHeifer, setIsHeifer] = useState(false)
    const [expectedBirthDate, setExpectedBirthDate] = useState(null)

    const handleToggleHeifer = () => setIsHeifer((prevIsHeifer) => !prevIsHeifer)

    const addCow = async values => {
        const { cowno, ear, tail, grpid } = values;
        try {
            const res = await doFetch({
                data: {
                    cowno,
                    ear: ear ? parseInt(ear) : null,
                    tail: tail ? parseInt(tail) : null,
                    grpid: grpid ? parseInt(grpid) : null,
                    heifer: isHeifer,
                    last_birth: moment(lastBirthDate).unix(),
                    expected_birth: moment(expectedBirthDate).unix(),
                }
            })
            if(res === 0) {
                const error = pathOr(null, ['fields', 'cowno', 'error'], modalData);
                if (error) {
                    setError({ cowno: error })
                }
            }
        } catch (err) {
            console.log("err", err)
        }
    }

    const validate = values => {
        const isEmptyNumber = compose(isEmpty, trim, defaultTo(''), prop('cowno'))
        if (isEmptyNumber(values)) {
            return {
                cowno: pathOr('', ['fields', 'cowno', 'hint'], modalData)
            }
        }
        return {}
    }

    const getInitialValues = () => {
        if (modalData) {
            return {
                cowno: "",
                ear: "",
                tail: "",
                grpid: compose(
                    toString,
                    defaultTo("0"),
                    prop("grp_id"),
                    head,
                    pathOr([], ['fields', 'group', 'data'])
                )(modalData)
            }
        } else {
            return {}
        }
    }

    const { values, errors, handleChange, handleSubmit, setError } = useForm({
        callback: addCow,
        validate: validate,
        initValues: getInitialValues()
    })

    const handleClickCancel = () => {
        onClickCancel()
    }

    const handleClickClose = () => {
        doFetchHerd && doFetchHerd()
        onClickCancel()
    }

    return (
        <Form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormHeader>
                {modalData && modalData.title}
            </FormHeader>
            <FormBody>
                {
                    (isEmpty(response.data) || response.data === 0) && modalData ?
                        <>
                            {
                                modalData.fields && modalData.fields.cowno &&
                                <FromInput
                                    label={modalData.fields.cowno.title}
                                    type='text'
                                    maxLength={8}
                                    placeholder={modalData.fields.cowno.placeholder}
                                    name='cowno'
                                    onChange={handleChange}
                                    value={values.cowno}
                                    error={errors.cowno}
                                    fullWidth
                                    margin="0 0 10px 0"
                                />
                            }
                            {
                                modalData.fields && modalData.fields.last_birth &&
                                <>
                                    <DatePicker
                                        label={modalData.fields.last_birth.title}
                                        selected={lastBirthDate}
                                        onChange={date => setLastBirthDate(date)}
                                        dateFormat="dd.MM.yyyy"
                                        maxDate={moment()._d}
                                        active={!isHeifer}
                                        onClick={() => setIsHeifer(false)}
                                    />
                                    <Checkbox
                                        checked={isHeifer}
                                        label={modalData.fields.last_birth['heifer-label']}
                                        onClick={handleToggleHeifer}
                                    />
                                </>
                            }
                            {
                                modalData.fields && modalData.fields.expected_birth &&
                                <DatePicker
                                    label={modalData.fields.expected_birth.title}
                                    selected={expectedBirthDate}
                                    onChange={date => setExpectedBirthDate(date)}
                                    dateFormat="dd.MM.yyyy"
                                />
                            }
                            {
                                modalData.fields && modalData.fields.ear &&
                                <FromSelect
                                    label={modalData.fields.ear.title}
                                    placeholder={modalData.fields.ear.placeholder}
                                    name='ear'
                                    onChange={handleChange}
                                    value={values.ear}
                                    error={errors.ear}
                                    options={modalData.fields.ear.data.map(option => ({ value: option, label: option }))}
                                />
                            }
                            {
                                modalData.fields && modalData.fields.tail &&
                                <FromSelect
                                    label={modalData.fields.tail.title}
                                    placeholder={modalData.fields.tail.placeholder}
                                    name='tail'
                                    onChange={handleChange}
                                    value={values.tail}
                                    error={errors.tail}
                                    options={modalData.fields.tail.data.map(option => ({ value: option, label: option }))}
                                />
                            }
                            {
                                modalData.fields && modalData.fields.group &&
                                <FromSelect
                                    label={modalData.fields.group.title}
                                    placeholder={modalData.fields.group.placeholder}
                                    name='grpid'
                                    onChange={handleChange}
                                    value={values.grpid}
                                    error={errors.grpid}
                                    options={parseMygrplist(modalData.fields.group.data)}
                                />
                            }
                            <Buttons>
                                <Button
                                    type='submit'
                                    fullWidth
                                    green
                                >
                                    {modalData.buttons.save}
                                </Button>
                                <Button
                                    type='button'
                                    fullWidth
                                    onClick={handleClickCancel}
                                >
                                    {modalData.buttons.cancel}
                                </Button>
                            </Buttons>
                        </>
                        :
                        <>
                            {
                                modalData &&
                                <>
                                    <FormBodyText>
                                        <Icon width='80' height='80' viewBox="0 0 24 24">
                                            <polyline points="20 6 9 17 4 12" />
                                        </Icon>
                                    </FormBodyText>
                                    <Button
                                        type='button'
                                        fullWidth
                                        onClick={handleClickClose}
                                    >
                                        {modalData.buttons.close}
                                    </Button>
                                </>
                            }
                        </>
                }
            </FormBody>
        </Form>
    )
}

export default FormAddCow
